import { useMemo } from "react";
import { TourDate } from "@/types/dto/pdp/pdp-dates-inclusions-pricing";
import { createTourDateFromApi, getOfferDetails } from "@/factories/pdp/pdp-dates-inclusions-pricing-factories";
import { DateMetadata } from "@/types/dto/pdp/pdp-contentful";

/**
 * Simple hook to extract date-related memoized calculations from the main PDP component
 * This keeps the main component cleaner by moving complex date logic into a focused hook
 */
export const useDateCalculations = (dateMetadata: DateMetadata, selectedTourDate?: TourDate | null) => {
  const currentPackageDateId = selectedTourDate?.packageDateId;
  
  // Transform API packageDateItems to component-ready tourDates
  const tourDates = useMemo(
    () => dateMetadata.packageDateItems.map(createTourDateFromApi),
    [dateMetadata.packageDateItems]
  );

  const defaultTourDate = useMemo(() => {
    if (!tourDates.length) return null;
    return (
      tourDates.find((date: { packageDateId: any; }) => date.packageDateId === dateMetadata.nextAvailablePackageDateId) ??
      tourDates[0]
    );
  }, [tourDates, dateMetadata.nextAvailablePackageDateId]);

  // Extract offer details from API data
  const offerDetails = useMemo(
    () => getOfferDetails(dateMetadata.packageDateItems.find(
      (item) => item.packageDateNeoId === currentPackageDateId
    )),
    [currentPackageDateId, dateMetadata.packageDateItems]
  );

  // Get special event data from selected date (only if selectedTourDate is provided)
  const specialEventData = useMemo(() => {
    if (!selectedTourDate?.specialDates?.length) return undefined;
    const specialEvent = selectedTourDate.specialDates.find((sd) => sd.title === "Special Event");
    if (!specialEvent) return undefined;
    return {
      title: specialEvent.title,
      description: specialEvent.description
    };
  }, [selectedTourDate]);

  // Check if selected date has a Special Event (only if selectedTourDate is provided)
  const selectedDateHasSpecialEvent =
    selectedTourDate?.specialDates?.some((sd) => sd.title === "Special Event") ?? false;

  return {
    tourDates,
    defaultTourDate,
    offerDetails,
    specialEventData,
    selectedDateHasSpecialEvent
  };
};

export default useDateCalculations;